/**
 * ============================================================================
 * CHECKBOX COMPONENT - 100% Ready
 * ============================================================================
 */

import React, { forwardRef } from 'react';

/**
 * Checkbox Component
 * @param {Object} props
 * @param {boolean} props.checked - Checked state
 * @param {Function} props.onChange - Change handler
 * @param {string} props.label - Label text
 * @param {string} props.error - Error message
 * @param {string} props.className - Additional classes
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.id - Checkbox ID
 * @param {string} props.name - Checkbox name
 * @param {string} props.size - Size (sm, md, lg)
 * @returns {JSX.Element}
 */
const Checkbox = forwardRef(
  (
    {
      checked,
      onChange,
      label,
      error,
      className = '',
      disabled = false,
      id,
      name,
      size = 'md',
      ...props
    },
    ref
  ) => {
    const sizeClasses = {
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-6 h-6',
    };

    const checkboxId = id || name || `checkbox-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div className={`flex flex-col ${className}`}>
        <label
          htmlFor={checkboxId}
          className={`
            flex items-center gap-3 cursor-pointer
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <div className="relative flex-shrink-0">
            <input
              ref={ref}
              type="checkbox"
              id={checkboxId}
              name={name}
              checked={checked}
              onChange={onChange}
              disabled={disabled}
              className={`
                ${sizeClasses[size] || sizeClasses.md}
                rounded border-2 border-gray-300 dark:border-gray-600
                bg-white dark:bg-gray-800
                text-blue-600 focus:ring-4 focus:ring-blue-500/20
                transition-all duration-200
                cursor-pointer
                disabled:cursor-not-allowed
                ${error ? 'border-red-500' : ''}
              `}
              aria-invalid={!!error}
              aria-describedby={error ? `${checkboxId}-error` : undefined}
              {...props}
            />
            {checked && (
              <svg
                className={`
                  absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                  ${sizeClasses[size] || sizeClasses.md}
                  text-white pointer-events-none
                `}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            )}
          </div>
          {label && (
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {label}
            </span>
          )}
        </label>
        {error && (
          <p
            id={`${checkboxId}-error`}
            className="mt-1 text-sm text-red-600 dark:text-red-400"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
